import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dropdown, Input, Popup } from 'components/UIElements'
import { pluralize } from 'utils/misc'
import STRINGS from 'utils/strings'

const DAY_RELATIVITY_OPTIONS = [
  { key: 'joins', text: STRINGS.visitScheduleJoinsStudy },
  { key: 'lastVisit', text: STRINGS.visitScheduleLastVisit },
  { key: 'completesVisit', text: STRINGS.visitScheduleCompletesVisit },
  { key: 'startsCycle', text: STRINGS.visitScheduleStartsCycle },
  { key: 'completesCycle', text: STRINGS.visitScheduleCompletesCycle },
]

const VisitDaySelector = props => {
  const {
    disabled,
    selectedVisitScheduleId,
    selectedCycleId,
    templateVisit,
    templateVisitErrors,
    updateVisitFields,
    updateVisitWindow,
    visitScheduleInitialData,
    isNewVisitVersion,
    studyVisitSchedule,
    isReadOnlyState,
  } = props
  const { id, visit_day, visit_window, visit_metadata, metadata, day, window, anchor_id } = templateVisit
  const deployment_option = visit_metadata?.deployment_option || metadata?.deployment_option
  const relative_visit_id = visit_metadata?.relative_visit_id || metadata?.relative_visit_id
  const relative_cycle_id = visit_metadata?.relative_cycle_id || metadata?.relative_cycle_id
  const visit_day_ui_num = visit_metadata?.visit_day_ui_num || metadata?.visit_day_ui_num
  const {
    visit_day: visitDayErr,
    visit_window: visitWindowErr,
    complete_cycle: completeCycleErr,
    complete_visit: completeVisitErr,
  } = templateVisitErrors

  const getAllCycles = () => {
    const arm = studyVisitSchedule?.find(item => item.id === selectedVisitScheduleId)
    return (
      arm?.phases?.reduce((acc, phase) => {
        return [...acc, ...phase?.cycles]
      }, []) ?? []
    )
  }

  const allCycles = getAllCycles()

  const getCycleOptions = () => {
    const notDummyCycles = allCycles?.filter(cycle => !cycle?.is_dummy)

    const formattedCycles = notDummyCycles?.map(cycle => ({
      key: cycle?.id,
      text: cycle?.name,
      cycleId: cycle?.id,
    }))

    return formattedCycles
  }

  const [selectedVisitDayRelativity, setSelectedVisitDayRelativity] = useState(DAY_RELATIVITY_OPTIONS[0].key)
  const [visitOptions, setVisitOptions] = useState([])
  const cycleOptions = getCycleOptions()
  const [selectedVisit, setSelectedVisit] = useState(null)
  const [selectedCycle, setSelectedCycle] = useState(null)
  const [hasVisitWindow, setHasVisitWindow] = useState(true)
  const [visitDayMap, setVisitDayMap] = useState({})
  const [dayVal, setDayVal] = useState(visit_day_ui_num ?? (isNewVisitVersion ? day : visit_day))

  const _disabled = !selectedVisitScheduleId || disabled || isReadOnlyState

  useEffect(() => {
    setVisitOptions(
      visitScheduleInitialData[selectedVisitScheduleId]?.visitOptions.filter(visit => visit?.key !== id) || [],
    )
    setVisitDayMap(visitScheduleInitialData[selectedVisitScheduleId]?.visitDayMap || [])
  }, [selectedVisitScheduleId])

  /**
   * This hook will update the visit_day value using the calculateVisitDay util func dependent to
   * what type of visit day relavity is chosen and what value is entered in the visit day field.
   */
  useEffect(() => {
    if (selectedCycleId) {
      setSelectedVisitDayRelativity(DAY_RELATIVITY_OPTIONS.find(({ key }) => key === 'startsCycle').key)
      updateVisitFields({
        key1: 'visit_metadata',
        value1: {
          ...visit_metadata,
          deployment_option: 'startsCycle',
        },
      })
    }
  }, [selectedVisitDayRelativity, selectedCycleId])

  useEffect(() => {
    if (id && deployment_option) {
      setSelectedVisitDayRelativity(deployment_option)
    }
  }, [])

  useEffect(() => {
    if (id && relative_visit_id) {
      setSelectedVisit(relative_visit_id)
    }
  }, [])

  useEffect(() => {
    if (id && relative_cycle_id) {
      setSelectedCycle(relative_cycle_id)
    }
  }, [])

  useEffect(() => {
    if (!deployment_option) {
      updateVisitFields({
        key1: 'visit_metadata',
        value1: {
          ...visit_metadata,
          deployment_option: DAY_RELATIVITY_OPTIONS[0].key,
        },
        key2: 'anchor_id',
        value2: '',
      })
    }
  }, [])

  const isCompleteVisitRelativity = selectedVisitDayRelativity === 'completesVisit'
  const isCompleteCycleRelativity = selectedVisitDayRelativity === 'completesCycle'

  const getRelativityOptions = () => {
    let options = DAY_RELATIVITY_OPTIONS

    if (!selectedCycleId) {
      options = options.filter(item => item.key !== 'startsCycle')
    }

    if (selectedCycleId) {
      options = options.filter(item => item.key === 'startsCycle')
    }

    if (!visitOptions?.length) {
      options = options.filter(item => item.key !== 'lastVisit' && item.key !== 'completesVisit')
    }

    if (isNewVisitVersion) {
      options = options.filter(item => item.key !== 'lastVisit')
    }

    if (!cycleOptions?.length) {
      options = options.filter(item => item.key !== 'completesCycle')
    }

    return options
  }

  return (
    <>
      <label className='label-small' htmlFor='visit-day-selection'>
        Visit Day
      </label>
      <div id='visit-day-selection'>
        <div className='flexed'>
          <Input
            disabled={_disabled || isReadOnlyState}
            id='visit-day-input'
            min='0'
            onChange={value => {
              setDayVal(value)
              const parsedDay = value === '' ? null : Number(value)
              updateVisitFields({
                key1: 'day',
                value1: parsedDay,
                key2: 'visit_day',
                value2: parsedDay,
              })
            }}
            value={dayVal || dayVal === 0 ? dayVal : ''}
            posttext={`${pluralize(dayVal, 'day', 'days', false)} since participant`}
            type='number'
            hasError={
              !!visitDayErr &&
              !(typeof visitDayErr === 'string' && visitDayErr.includes('study duration') && visitDayErr.includes('cannot exceed it'))
            }
            errorText={
              typeof visitDayErr === 'string' && visitDayErr.includes('cannot exceed it') ? '' : visitDayErr
            }
          />
          <Dropdown
            disabled={_disabled || isReadOnlyState}
            id='visit-relativity-selector'
            options={getRelativityOptions()}
            onSelect={item => {
              setSelectedVisitDayRelativity(item.key)
              const anchorId = ['lastVisit', 'joins', 'startsCycle']?.includes(item.key) ? '' : anchor_id
              const relative_visit_id =
                item.key === 'lastVisit'
                  ? visitOptions.filter(visit => visit.key !== id).sort((a, d) => d.visitDay - a.visitDay)[0]?.key
                  : undefined
              updateVisitFields({
                key1: 'visit_metadata',
                value1: {
                  ...visit_metadata,
                  deployment_option: item.key,
                  relative_visit_id,
                },
                key2: 'anchor_id',
                value2: anchorId,
              })
            }}
            selected={selectedVisitDayRelativity}
          />
        </div>
        {(isCompleteVisitRelativity || !hasVisitWindow) && (
          <div className='flexed'>
            {isCompleteVisitRelativity ? (
              <Dropdown
                id='relative-visit-selector'
                disabled={_disabled || isReadOnlyState}
                hasError={completeVisitErr && !selectedVisit}
                errorText={completeVisitErr}
                options={visitOptions}
                selected={selectedVisit}
                onSelect={item => {
                  setSelectedVisit(item.key)
                  updateVisitFields({
                    key1: 'visit_metadata',
                    value1: {
                      ...visit_metadata,
                      relative_visit_id: item.key,
                    },
                    key2: 'anchor_id',
                    value2: item?.cycleId,
                  })
                }}
                placeholder='Select a visit'
              />
            ) : (
              <div />
            )}
            {!hasVisitWindow && (
              <Button
                id='add-visit-window-btn'
                disabled={_disabled || isReadOnlyState}
                link
                content='+ add visit window'
                onClick={() => {
                  setHasVisitWindow(true)
                }}
              />
            )}
          </div>
        )}
        {isCompleteCycleRelativity ? (
          <div className='flexed'>
            <Dropdown
              id='relative-cycle-selector'
              disabled={_disabled || isReadOnlyState}
              hasError={completeCycleErr && !selectedCycle}
              errorText={completeCycleErr}
              options={cycleOptions}
              selected={selectedCycle}
              onSelect={item => {
                setSelectedCycle(item.key)
                updateVisitFields({
                  key1: 'visit_metadata',
                  value1: {
                    ...visit_metadata,
                    relative_cycle_id: item.key,
                  },
                  key2: 'anchor_id',
                  value2: item?.cycleId,
                })
              }}
              placeholder='Select a cycle'
            />
          </div>
        ) : null}
        {hasVisitWindow && (
          <div className='visit-window'>
            <span className={disabled ? 'disabled' : 'flexed start-justified'}>
              <label className='label-small' htmlFor='visit-window-field'>
                {STRINGS.visitWindow}
              </label>
              <Popup
                className='visit-window-popup'
                align='left'
                position='bottom'
                hover
                noPointer
                dark
                trigger={<i className='fas fa-info-circle' />}>
                <p>{STRINGS.visitWindowInfo}</p>
              </Popup>
            </span>

            <Input
              disabled={disabled || isReadOnlyState}
              id='visit-window-field'
              min={0}
              onlyInteger={true}
              posttext='days of flexibility around the visit day.'
              pretext='Allow +/-'
              type='number'
              onChange={value => {
                updateVisitWindow({ value })
              }}
              value={visit_window || window}
              hasError={
                ((visit_window || window) < 0 ||
                  (visit_window || window) === null ||
                  (visit_window || window) === undefined ||
                  (visit_window || window) === '') &&
                visitWindowErr
              }
              errorText={visitWindowErr}
            />
          </div>
        )}
      </div>
    </>
  )
}

VisitDaySelector.propTypes = {
  disabled: PropTypes.bool,
  selectedVisitScheduleId: PropTypes.string,
  templateVisit: PropTypes.shape({
    visit_day: PropTypes.number,
    visit_window: PropTypes.number,
  }),
  templateVisitErrors: PropTypes.objectOf(PropTypes.string),
  updateVisit: PropTypes.func,
  visitScheduleInitialData: PropTypes.shape({
    visitOptions: PropTypes.array,
  }),
}

export default VisitDaySelector
